"use client";

import { ZodRawShape, z } from "zod";
import { formControls, schemas } from "./schemas";
import FormField from "./FormField";
import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import {
  createFormResponseDraft,
  getPrefillFromPriorSubmissions,
  saveFormResponse,
} from "@/helpers/server-actions/users-collection";
import { getCycleDocumentId, getCycleOpensOn, getFormCycle, isCycleOpen, resolveFormKey } from "./form-cycles";
import { getFormBranching } from "./form-branching";
import FilmFormChooser from "./FilmFormChooser";
import { useApplicantUser } from "../useApplicantUser";
import { processFormResponse } from "@/helpers/utils/utility-functions";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "@/redux/features/app-slice";
import { FormResponseStatus } from "@/models/form-types";
import useKeyboardVisibility from "@/hooks/useKeyboardVisibility";
import Button from "@/app/_components/Button/Button";
import { formSlugs } from "@/helpers/utils/form-constants";
import { trackFormEvent } from "@/lib/posthog";
import { trackApplicationConversion } from "@/lib/google-ads";
import { readAttribution } from "@/lib/attribution";
import { auth } from "@/helpers/firebase/firebase";
import { VillageRecruitmentStage } from "@/helpers/utils/village-intake";
import { getVillageConfirmation, getVillagePackagePrefill } from "@/helpers/utils/village-intake";
import { getFilmConfirmation } from "@/helpers/utils/film-constants";
import { getStartupAlphaConfirmation } from "@/helpers/utils/startup-alpha-constants";

type FormProps = {
  slug: string[];
};

export default function Page({ params, searchParams = {} }: { params: FormProps; searchParams?: Record<string, string | string[] | undefined> }) {
  const applicant = useApplicantUser();
  // The legacy single film form now routes people to the three film forms.
  if (resolveFormKey(params.slug.join("/")) === formSlugs.film) return <FilmFormChooser />;
  return <ApplicationPage key={`${params.slug.join("/")}:${applicant.userData?.uid ?? "pending"}`} params={params} {...applicant}
    packageHint={typeof searchParams.package === "string" ? searchParams.package : undefined}
    sourceHint={searchParams.source === "village-expo" ? "village-expo" : undefined} />;
}

function ApplicationPage({ params, userData, authError, packageHint, sourceHint }: { params: FormProps; packageHint?: string; sourceHint?: string } & ReturnType<typeof useApplicantUser>) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [formData, setFormData] = useState<any>(null);
  const [triggerSaveAndNext, setTriggerSaveAndNext] = useState<boolean>(false);
  const acknowledgeSubmitTrigger = useCallback(() => setTriggerSaveAndNext(false), []);
  const [isFormCompleted, setIsFormCompleted] = useState<boolean>(false);
  const [submissionStage, setSubmissionStage] = useState<VillageRecruitmentStage>();
  const [notificationFailed, setNotificationFailed] = useState(false);
  const [prefillCount, setPrefillCount] = useState<number>(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [blockedReason, setBlockedReason] = useState<
    "submitted" | "closed" | null
  >(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const saving = useRef(false);
  const introStarted = useRef(false);
  const [ready, setReady] = useState(false);
  const pendingData = useRef<any>(null);
  const dispatch = useDispatch();
  let { slug } = params;

  // join by slash
  const id = resolveFormKey(slug.join("/"));
  const firebaseDocId = id.replaceAll("/", "_");
  const cycle = getFormCycle(id);
  const festivalYear = cycle?.festivalYear ?? null;
  const cycleOpen = cycle ? isCycleOpen(cycle) : false;
  const opensOn = cycle ? getCycleOpensOn(cycle) : null;
  const storedFormId = firebaseDocId;
  const activeDocumentId =
    cycle ? getCycleDocumentId(cycle) : "";

  const allSchema = schemas[id as keyof typeof schemas];
  const allControls = formControls[id as keyof typeof schemas];
  const branching = getFormBranching(id);
  const schema = branching
    ? branching.select(allSchema ?? {}, formData?.[branching.field])
    : allSchema;
  const form = z.object(schema as ZodRawShape);
  const controls = branching
    ? branching.select(allControls ?? {}, formData?.[branching.field])
    : allControls;
  const villageConfirmation = getVillageConfirmation(id, submissionStage);
  const filmConfirmation = getFilmConfirmation(id, formData);
  const startupAlphaConfirmation = getStartupAlphaConfirmation(id);
  const totalQuestions =
    controls &&
    Object.entries(controls).filter((x) => x[1].type != "intro").length;
  const isIntroPage =
    !!controls && Object.values(controls).some((control) => control.type === "intro");
  const router = useRouter();
  const bgImageRef = useRef<HTMLImageElement>(null);
  const { isKeyboardVisible, keyboardOffset } = useKeyboardVisibility();
  const formTracked = useRef(false);

  useEffect(() => {
    let active = true;
    setReady(false);
    if (!allSchema || !allControls) {
      const aliases: Record<string, string> = {
        speaker: "general/speaker",
        gaming: "gaming/developer",
        music: "music/artist",
        fashion: "fashion/designer",
      };
      if (aliases[id]) {
        router.replace(`/form/${aliases[id]}`);
      } else {
        router.replace("/dashboard");
      }
    } else {
      if (id === formSlugs.gamingEsportOverwatch) {
        router.replace("/form/gaming-esports-overwatch");
        return;
      }
      if (!formTracked.current) {
        const initialControls = branching ? branching.select(allControls, undefined) : allControls;
        trackFormEvent('start', id, { total_questions: Object.values(initialControls).filter((control) => control.type !== "intro").length });
        formTracked.current = true;
      }
      if (userData) {
        if (!cycleOpen) {
          setBlockedReason("closed");
          return;
        }

        // Create-if-absent runs as one Firestore transaction, so the
        // "does a record exist" check and the draft write cannot interleave
        // with another tab. Legacy volunteer records are read-only history.
        createFormResponseDraft(
          userData,
          activeDocumentId,
          storedFormId,
          festivalYear
        )
          .then(async (result) => {
            if (!active) return;
            setReady(true);
            if (!result.created) {
              if (result.status === FormResponseStatus.NOT_SUBMITTED) {
                setFormData((prev: any) => ({
                  ...(id === formSlugs.vendor ? getVillagePackagePrefill(packageHint) : {}),
                  ...result.data,
                  ...prev,
                }));
                return;
              }
              // IN_REVIEW or any decided status: never overwrite with a draft.
              setBlockedReason("submitted");
              return;
            }

            if (id === formSlugs.vendor) {
              setFormData((prev: any) => ({ ...getVillagePackagePrefill(packageHint), ...prev }));
            }

            if (!userData.isAnonymous && allControls) {
              const fieldKeys = Object.keys(allControls).filter(
                (k) => allControls[k].type !== "intro"
              );
              const profileSeed: Record<string, string> = {};
              if (fieldKeys.includes("firstName") && userData.firstName)
                profileSeed.firstName = userData.firstName;
              if (fieldKeys.includes("lastName") && userData.lastName)
                profileSeed.lastName = userData.lastName;
              if (fieldKeys.includes("email") && userData.email)
                profileSeed.email = userData.email;
              if (fieldKeys.includes("phone") && userData.phoneNumber)
                profileSeed.phone = userData.phoneNumber;

              getPrefillFromPriorSubmissions(userData.uid, fieldKeys)
                .then((result) => {
                  if (!active) return;
                  const merged = { ...profileSeed, ...result.data };
                  const count = Object.keys(merged).length;
                  if (count > 0) {
                    setFormData((prev: any) =>
                      prev ? { ...merged, ...prev } : merged
                    );
                    setPrefillCount(count);
                  }
                })
                .catch(() => {
                  if (!active) return;
                  // Prefill is a convenience; a missing index or read error
                  // must not block a fresh application.
                  const count = Object.keys(profileSeed).length;
                  if (count > 0) {
                    setFormData((prev: any) =>
                      prev ? { ...profileSeed, ...prev } : profileSeed
                    );
                    setPrefillCount(count);
                  }
                });
            }
          })
          .catch((error) => {
            if (!active) return;
            if (error.message === "closed") {
              setBlockedReason("closed");
              return;
            }
            trackFormEvent("error", id, { stage: "draft_create" });
            setSaveError(
              "We couldn't start your application. Check your connection and refresh to try again."
            );
          });
      }
    }
    return () => { active = false; };
  }, [userData, id, activeDocumentId, storedFormId, festivalYear, cycleOpen, allControls, allSchema, router, packageHint, branching]);

  const saveAndNext = async (data: any) => {
    // One in-flight save at a time: a double click, Enter + click, or a retry
    // during a slow write must not produce overlapping step/final writes.
    if (!controls || saving.current || isFormCompleted || blockedReason) return;
    if (!cycle || !isCycleOpen(cycle)) {
      setBlockedReason("closed");
      return;
    }
    if (!ready) return;
    if (!data) {
      if (!isIntroPage || questionIndex !== 0 || introStarted.current) return;
      introStarted.current = true;
    }
    setSaveError(null);
    const isLastStep =
      (isIntroPage && questionIndex >= totalQuestions) ||
      (!isIntroPage && questionIndex >= totalQuestions - 1);

    if (!!data) {
      if (branching && branching.field in data) {
        data = { ...data, ...branching.clear(data[branching.field]) };
      }
      pendingData.current = data;
      // Retain answers locally before attempting the write so a failed save
      // never discards what the applicant typed.
      setFormData((prev: any) => (prev ? { ...prev, ...data } : { ...data }));

      if (!userData) {
        trackFormEvent("error", id, { stage: "no_session" });
        setSaveError(
          "We couldn't sign you in to save your answers. Refresh the page and try again."
        );
        return;
      }

      saving.current = true;
      setIsSaving(true);
      try {
        // Upload and persist are one recoverable operation: a rejected
        // Storage upload lands in the same alert/retry path as a failed write.
        let transformedFormResponse: Record<string, unknown>;
        try {
          transformedFormResponse = await processFormResponse({
            formResponse: { ...data },
            userId: userData.uid,
          });
        } catch {
          trackFormEvent("error", id, {
            stage: "upload",
            question_index: questionIndex,
            total_questions: totalQuestions || 0,
          });
          setSaveError(
            "Your file could not be uploaded. Your answers are still here, so please try again."
          );
          return;
        }

        // Validate the whole visible intake before final submission, including
        // explicit consent. Resumed drafts may predate the recruitment schema.
        if (isLastStep && villageConfirmation) {
          const completed = form.safeParse({ ...formData, ...transformedFormResponse });
          if (!completed.success) {
            const firstField = String(completed.error.issues[0]?.path[0] ?? "");
            const fieldIndex = Object.keys(schema).indexOf(firstField);
            if (fieldIndex >= 0) setQuestionIndex(fieldIndex + (isIntroPage ? 1 : 0));
            setSaveError("Please complete the required answers and consent before submitting.");
            return;
          }
          transformedFormResponse = {
            ...completed.data,
            consentVersion: "village-recruitment-2027-v1",
            ...(id === formSlugs.resumeUpload ? { resumeSharingPreference: "separate_approval_required" } : {}),
          };
        }

        const result = await saveFormResponse(
          userData,
          activeDocumentId,
          {
            ...transformedFormResponse,
            ...(villageConfirmation && sourceHint ? { intakeSource: sourceHint } : {}),
            ...(isLastStep && readAttribution() ? { attribution: readAttribution() } : {}),
            status: isLastStep
              ? FormResponseStatus.IN_REVIEW
              : FormResponseStatus.NOT_SUBMITTED,
          },
          { formId: storedFormId, festivalYear }
        );
        if (!result.ok) {
          if (result.reason === "closed") {
            setBlockedReason("closed");
            return;
          }
          if (result.reason === "already_submitted") {
            // Another tab already submitted (or a reviewer decided) this
            // application; this stale tab must not downgrade or overwrite it.
            pendingData.current = null;
            setBlockedReason("submitted");
            return;
          }
          trackFormEvent("error", id, {
            stage: isLastStep ? "submit" : "save",
            question_index: questionIndex,
            total_questions: totalQuestions || 0,
          });
          setSaveError(
            isLastStep
              ? "Your application could not be submitted. Your answers are still here, so please try again."
              : "Your answer could not be saved. Please try again."
          );
          return;
        }
        if (result.recruitmentStage) setSubmissionStage(result.recruitmentStage);
      } catch {
        setSaveError("Your answer could not be saved. Your answers are still here, so please try again.");
        return;
      } finally {
        saving.current = false;
        setIsSaving(false);
      }
    }
    pendingData.current = null;

    if (isLastStep) {
      // form completed
      if (userData) {
        dispatch(startLoader("Submitting the form"));
        dispatch(stopLoader());
        setIsFormCompleted(true);
        trackFormEvent('submit', id, { total_questions: totalQuestions || 0 });
        trackFormEvent('success', id);
        trackApplicationConversion(id);

        void (async () => {
          const token = await auth.currentUser?.getIdToken();
          if (!token) throw new Error("Notification session unavailable");
          const response = await fetch("/api/form-notification", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ documentId: activeDocumentId }),
          });
          if (!response.ok) throw new Error("Notification delivery failed");
        })().catch(() => {
          setNotificationFailed(true);
          trackFormEvent("error", id, { stage: "notification" });
        });
      }
    } else {
      setTriggerSaveAndNext(false);
      setQuestionIndex((prev) => {
        trackFormEvent('progress', id, {
          question_index: prev + 1,
          total_questions: totalQuestions || 0,
        });
        return prev + 1;
      });
    }
  };

  const goNext = (
    isIntro: boolean = false,
    e?: React.MouseEvent<HTMLButtonElement>
  ) => {
    e?.preventDefault();
    if (isIntro) {
      saveAndNext(null);
    } else {
      setTriggerSaveAndNext(true);
    }
  };

  const goBack = () => {
    if (saving.current) return;
    const prevQuestionIndex = questionIndex - 1;
    if (isIntroPage) {
      if (prevQuestionIndex <= 0) {
        return;
      }
    } else {
      if (prevQuestionIndex < 0) {
        return;
      }
    }
    setQuestionIndex((prev) => prev - 1);
  };

  const renderFormFields = (zodSchema: typeof form) => {
    const fields = [];
    const introControl = controls?.["intro"];
    if (introControl) {
      fields.push(
        <div className="md:max-w-[38.938rem]">
          <div className="flex justify-center items-center">
            <h2 className="text-2xl font-semibold text-custom-gray text-center break-words mx-10">
              {introControl.label}
            </h2>
          </div>

          {introControl.notes &&
            introControl.notes.map((x, i) => (
              <div
                className="text-custom-gray text-base font-normal text-center mt-6"
                key={i}
              >
                {x}
              </div>
            ))}

          {prefillCount > 0 && (
            <div className="flex items-center justify-center gap-2 mt-6 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span className="text-sm text-green-800">
                {prefillCount} {prefillCount === 1 ? "answer has" : "answers have"} been pre-filled from your previous submissions
              </span>
            </div>
          )}

          <div className="flex flex-col justify-center items-center">
            <div className="flex items-center gap-4 relative">
              <button
                className="md:w-[16.875rem] w-[9.675rem] flex justify-center items-center bg-black text-white text-sm p-2 mt-6 rounded-full md:mb-6 mb-6 hover:bg-white hover:text-black hover:border hover:border-gray-300"
                type="button"
                disabled={!ready || isSaving}
                onClick={() => saveAndNext(null)}
              >
                GET STARTED
              </button>

              <Image
                height={14}
                width={80}
                className="md:block hidden"
                style={{ position: "absolute", right: -100 }}
                alt="c-tribe"
                src="/images/form/enter_icon.svg"
              />
            </div>

            <div className="flex items-center md:gap-2 gap-1">
              <Image
                height={15}
                width={15}
                alt="c-tribe"
                src="/images/form/clock_icon.svg"
              />
              <span className="md:text-lg text-sm font-normal text-custom-gray">
                Takes{" "}
                {id === formSlugs.villageInterest
                  ? "about 2 minutes to complete"
                  : id === formSlugs.vendor ? "about 5 minutes to complete"
                  : id === formSlugs.techStartupAlpha ? "about 10 minutes to complete"
                  : id.includes(formSlugs.innovationZone)
                  ? "less than 1 minute to complete"
                  : "10+ minutes to complete"}
              </span>
            </div>
          </div>
        </div>
      );
    }
    let questionI = 0;
    for (const [fieldKey, fieldSchema] of Object.entries(zodSchema.shape)) {
      questionI++;
      const key = fieldKey as keyof typeof schema;
      const formControl = controls?.[key];
      if (!formControl) {
        continue;
      }
      fields.push(
        <FormField
          schema={{ [fieldKey]: fieldSchema }}
          control={formControl}
          fieldKey={fieldKey}
          key={fieldKey}
          index={questionI}
          totalQuestions={totalQuestions}
          onSubmit={saveAndNext}
          value={formData?.[fieldKey]}
          triggerSaveAndNext={triggerSaveAndNext}
          onSubmitTriggered={acknowledgeSubmitTrigger}
        />
      );
    }
    return fields;
  };

  const handleDoneClick = () => {
    if (userData?.isAnonymous) {
      window.close();
      // If window.close() doesn't work (not opened by script), go to main site
      window.location.href = "https://ctribefestival.com";
    } else {
      router.push("/dashboard");
    }
  };

  const keyboardActions = useRef({ start: () => {}, done: () => {} });
  keyboardActions.current = { start: () => { void saveAndNext(null); }, done: handleDoneClick };
  useEffect(() => {
    if (!isFormCompleted && !(questionIndex === 0 && isIntroPage)) return;
    const handleEnter = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      if (isFormCompleted) keyboardActions.current.done();
      else keyboardActions.current.start();
    };
    document.addEventListener("keypress", handleEnter);
    return () => document.removeEventListener("keypress", handleEnter);
  }, [questionIndex, isIntroPage, isFormCompleted]);

  const formType = () => {
    if (id === formSlugs.villageInterest) return "vendor";
    return id.split("/")[0];
  };

  if (!form.shape || !controls) {
    return <></>;
  }

  return (
    <div className="formLayout-background-container">
      {
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
          <Image
            alt="Background Image"
            src={
              slug
                ? `/images/form/${formType()}_background_image.svg`
                : "/images/form/tech_background_image.svg"
            }
            ref={bgImageRef}
            onError={(e) => {
              if (bgImageRef?.current) {
                bgImageRef.current.src =
                  "/images/form/vendor_background_image.svg";
              }
            }}
            layout="fill"
            objectFit="cover"
          />
        </div>
      }

      <div className="loginLayout-content">
        <Image
          height={44}
          width={100}
          style={{ position: "absolute", left: 30, top: 40 }}
          alt="c-tribe"
          src="/images/C-Tribe_Main_Transparent_Logo.svg"
        />

        <div className="overflow-y-auto md:flex items-center justify-center h-screen md:w-full w-screen md:mt-0 mt-24 md:mb-0 mb-0">
          <div className="flex items-center justify-center md:mt-0 md:mx-0 md:mb-20 mb-36">
            <div className="relative flex flex-col my-4 mx-6 p-0 shadow-2xl rounded-2xl md:space-y-0 bg-white bg-opacity-70 md:min-w-[38.938rem] w-full md:max-w-[75%]">
              <div className="flex justify-start flex-col md:mx-[2.5rem] mx-[1.5rem] md:mt-[3.188rem] mt-[2.875rem] md:mb-[1.875rem] mb-[7.175rem]">
                {blockedReason || !cycleOpen ? (
                  <div className="flex flex-col items-center text-custom-gray">
                    <div className="text-2xl font-bold leading-none text-center">
                      {blockedReason === "submitted"
                        ? "You've already submitted this application"
                        : opensOn
                        ? `This application opens ${opensOn}`
                        : "This application is closed"}
                    </div>
                    <div className="text-sm mt-5 text-center">
                      {blockedReason === "submitted"
                        ? "We have your submission and will be in touch by email. Questions? Reach us at "
                        : opensOn
                        ? `Applications open on ${opensOn}. Please come back then. Questions? Reach us at `
                        : "This intake is no longer accepting submissions. For current opportunities, contact us at "}
                      <a
                        href="mailto:hello@ctribefestival.com"
                        className="underline font-medium"
                      >
                        hello@ctribefestival.com
                      </a>
                      .
                    </div>
                    <div className="w-1/3 mt-5">
                      <Button
                        type="button"
                        disabled={false}
                        label="done"
                        onClick={handleDoneClick}
                      />
                    </div>
                  </div>
                ) : !isFormCompleted ? (
                  <>
                    {(saveError || authError) && (
                      <div
                        role="alert"
                        className="flex flex-col items-center gap-2 mb-6 px-4 py-3 bg-red-50 border border-red-200 rounded-lg"
                      >
                        <span className="text-sm text-red-800 text-center">
                          {saveError || authError}
                        </span>
                        {pendingData.current && (
                          <button
                            type="button"
                            disabled={isSaving}
                            className="text-sm font-medium underline text-red-900"
                            onClick={() => saveAndNext(pendingData.current)}
                          >
                            Try again
                          </button>
                        )}
                      </div>
                    )}
                    {renderFormFields(form)[questionIndex]}
                  </>
                ) : (
                  <div className="flex flex-col items-center text-custom-gray">
                    <div className="text-2xl font-bold leading-none">
                      Thank You for Your Submission!
                    </div>
                    <div className="text-sm mt-5">
                      {!id.includes(formSlugs.innovationZone) && (
                        <div>
                          We appreciate your interest in being a part of the
                          C-Tribe community. Your input is valuable to us, and
                          we are committed to responding promptly.
                        </div>
                      )}
                      <div className="mt-4">
                        {villageConfirmation ? villageConfirmation : filmConfirmation ? filmConfirmation : startupAlphaConfirmation ? startupAlphaConfirmation : id.includes("/exec-luncheon/") ? (
                          <>
                            Your ticket will be available in your inbox and your
                            Digital Passport.
                          </>
                        ) : id.includes(formSlugs.innovationZone) ? (
                          <>
                            you&apos;re all set! See you at C-Tribe! If you have
                            any questions, please don&apos;t hesitate to reach
                            out to us at&nbsp;
                            <a
                              href="mailto:hello@ctribefestival.com"
                              className="underline font-medium"
                            >
                              hello@ctribefestival.com.
                            </a>
                          </>
                        ) : (
                          <>
                            We will be in touch via email
                            regarding our decision. If you have any questions,
                            please don&apos;t hesitate to reach out to us
                            at&nbsp;
                            <a
                              href="mailto:hello@ctribefestival.com"
                              className="underline font-medium"
                            >
                              hello@ctribefestival.com.
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                    {notificationFailed && (
                      <p role="status" className="text-sm mt-4">
                        Your submission is saved, but we could not confirm email receipt delivery.
                        You do not need to submit again. For help, contact{" "}
                        <a href="mailto:hello@ctribefestival.com" className="underline">hello@ctribefestival.com</a>.
                      </p>
                    )}
                    <div className="mt-5 flex w-full flex-wrap items-center justify-center gap-4">
                        <Button
                          type="button"
                          disabled={false}
                          label="done"
                          width="min(8.75rem, 100%)"
                          onClick={handleDoneClick}
                        />
                      <div aria-hidden="true" className="hidden shrink-0 md:block">
                        <Image
                          height={14}
                          width={80}
                          alt="c-tribe"
                          src="/images/form/enter_icon.svg"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {(!isIntroPage || (isIntroPage && questionIndex > 0)) &&
          !isFormCompleted &&
          cycleOpen &&
          !blockedReason && (
            <>
              <div
                style={{ position: "absolute", right: 48, bottom: 40 }}
                className="md:flex hidden gap-2"
              >
                <button type="button" disabled={isSaving} onClick={goBack}>
                  <Image
                    height={45}
                    width={45}
                    alt="c-tribe"
                    src="/images/form/back_button.svg"
                  />
                </button>
                <button type="button" disabled={!ready || isSaving} onClick={(e) => goNext(false, e)}>
                  <Image
                    height={45}
                    width={45}
                    alt="c-tribe"
                    src="/images/form/next_button.svg"
                  />
                </button>
              </div>

              <div
                className={`flex justify-center items-center w-full h-[3.375rem] bg-black bg-opacity-50 md:hidden fixed`}
                style={{
                  bottom: isKeyboardVisible ? `${keyboardOffset}px` : "0",
                }}
              >
                <div className="flex justify-center items-center gap-0 relative">
                  <div
                    onClick={goBack}
                    className="flex justify-center items-center w-8 h-8 bg-black rounded-full"
                    style={{ position: "absolute", left: -40 }}
                  >
                    <Image
                      height={14}
                      width={14}
                      alt="c-tribe"
                      src="/images/form/back_arrow_button.svg"
                    />
                  </div>

                  <button
                    className="md:w-[16.875rem] w-[17.5rem] h-9 flex justify-center items-center bg-black text-white text-lg font-medium p-2 mt-0 rounded-full "
                    type="button"
                    disabled={!ready || isSaving}
                    onClick={(e) => goNext(false, e)}
                  >
                    {questionIndex + 1 >= Object.keys(controls).length
                      ? "SUBMIT"
                      : "NEXT"}
                  </button>
                </div>
              </div>
            </>
          )}
      </div>
    </div>
  );
}
